import React, { useCallback, useEffect, useState } from 'react';
import styled from "styled-components";
import { OfficeEntity } from '@features/desks/services/AdvanceHotDeskingService';
import { BodyRegular } from '@shared/ui/atoms/fonts/Body';
import { FlatContentCard } from '@shared/ui/atoms/FlatContentCard';
import { HeadlineMedium } from '@shared/ui/atoms/fonts/Headline';
import { PrimaryButton } from '@shared/ui/atoms/buttons/PrimaryButton';
import TextField from '@shared/ui/atoms/TextField';
import { agent } from "@shared/services/agent";
import { useDispatch } from "react-redux";
import { failureNotification } from "@shared/store/notification.duck";
import Colours from '@shared/ui/atoms/Colours';
import Icon, { IconTypes } from '@shared/ui/atoms/icon/Icon';
import { Row } from '@shared/ui/atoms/StructuralLayout';
import { ButtonSize } from '@shared/ui/atoms/buttons/ButtonStyling';

interface NetworkMapping {
  id: number;
  networkName: string;
  officeId: number;
}

export function AutomaticCheckinPanel(props: Props) {
  const { office } = props;
  const [networkMappings, setNetworkMappings] = useState<NetworkMapping[]>([]);
  const [newNetworkName, setNewNetworkName] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const dispatch = useDispatch();

  const fetchMappings = useCallback(async () => {
    try {
      const data = await agent.requests.get(`/api/office/${office.id}/network-mappings`);
      setNetworkMappings(data);
    } catch {
      // Silently fail on load
    }
  }, [office.id]);

  useEffect(() => {
    fetchMappings();
  }, [fetchMappings]);

  const handleAdd = async () => {
    const trimmed = newNetworkName.trim();
    if (!trimmed) return;

    try {
      setIsSaving(true);
      const saved = await agent.requests.post(`/api/office/${office.id}/network-mappings`, { networkName: trimmed });
      setNetworkMappings(prev => [...prev, saved]);
      setNewNetworkName('');
    } catch (err: any) {
      const message = err?.response?.body?.message || 'This network name is already mapped to an office';
      dispatch(failureNotification(message));
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await agent.requests.del(`/api/office/${office.id}/network-mappings/${id}`);
      setNetworkMappings(prev => prev.filter(m => m.id !== id));
    } catch {
      dispatch(failureNotification('Failed to remove network mapping'));
    }
  };

  return (
    <Container>
      <HeadlineMedium style={{marginBottom: 24}}>Automatic Check-in</HeadlineMedium>
      <FlatContentCard style={{marginBottom: 32}}>
        <BodyRegular weight={600}>How it works</BodyRegular>
        <BodyRegular>Map Wi-Fi network names to this office. When a device connects to a mapped network, the user will be automatically checked in.</BodyRegular>
      </FlatContentCard>

      <BodyRegular weight={600} style={{marginBottom: 12}}>Network Names</BodyRegular>

      {networkMappings.map(mapping => (
        <MappingRow key={mapping.id}>
          <BodyRegular weight={500}>{mapping.networkName}</BodyRegular>
          <Icon icon={IconTypes.Close} color="grey" onClick={() => handleDelete(mapping.id)} />
        </MappingRow>
      ))}

      {networkMappings.length === 0 && (
        <BodyRegular colour={Colours.darkGrey} style={{marginBottom: 12}}>No network names mapped to this office yet.</BodyRegular>
      )}

      <AddRow>
        <TextField value={newNetworkName}
                   onChange={setNewNetworkName}
                   placeholder="textinput.network-name-placeholder"
                   style={{flex: 1, marginBottom: 0, marginRight: 12}} />
        <PrimaryButton text="Add" click={handleAdd} disabled={isSaving || !newNetworkName.trim()} size={ButtonSize.Small} />
      </AddRow>
    </Container>
  );
}

interface Props {
  office: OfficeEntity;
}

const Container = styled.div``

const MappingRow = styled(Row)`
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  background-color: ${Colours.veryLightGrey};
  border-radius: 8px;
  margin-bottom: 8px;
`

const AddRow = styled(Row)`
  align-items: flex-end;
  margin-top: 12px;
`
